import { Component, DestroyRef, inject, output, ViewEncapsulation } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import {
    ActionEventArgs,
    CellClickEventArgs,
    DayService,
    DragAndDropService,
    EventClickArgs,
    EventRenderedArgs,
    EventSettingsModel,
    GroupModel,
    MonthService,
    PopupOpenEventArgs,
    ResizeService,
    TimelineViewsService,
    Timezone,
    WeekService,
    WorkHoursModel,
    WorkWeekService,
} from "@syncfusion/ej2-angular-schedule";
import { BehaviorSubject, combineLatest, EMPTY, Observable, of } from "rxjs";
import { catchError, filter, map, shareReplay, switchMap, take, tap } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { SwissSettingsService } from "src/app/services/swiss-settings.service";
import { CalendarService } from "src/app/services/calendar.service";
import { DatumFormatPipe } from "src/app/pipes/datum-format.pipe";
import { MessageBoxService } from "src/app/services/message-box.service";
import { UserAuthenticationService } from "src/app/services/user-authentication.service";
import { MainDataModels } from "src/app/models/maindata-models";
import { TerminEintragComponent } from "../termin-eintrag/termin-eintrag.component";

interface ScheduleEvent {
    Id: string;
    Subject: string;
    StartTime: Date;
    EndTime: Date;
    IsAllDay: boolean;
    UserName: string;
    RecurrenceRule?: string;
    RecurrenceException?: string;
    /** Set on the appointment the user has selected; read back in `onEventRendered` to paint it green. */
    Selected?: boolean;
}

interface ResourceItem {
    Mitarbeiter: string;
    Farbe: string;
    UserName: string;
}

export interface TimeRange {
    start: Date;
    end: Date;
}

const ZURICH = "Europe/Zurich";
const PASTEL_TONES = ["#957DAD", "#FEC8D8", "#C1E7E3", "#A0DB9E", "#B3CBE3"];
const DEFAULT_SUBJECT = "Kein Titel";
const SELECTED_COLOR = "green";
const SUCCESS_TITLE_KEY = "MAIN_APP_PENDENZEN.messageTitle.SUCCESS";
const ERROR_TITLE_KEY = "MAIN_APP_PENDENZEN.messageTitle.ERROR";

@Component({
    selector: "app-calendar",
    templateUrl: "./calendar.component.html",
    styleUrls: ["./calendar.component.scss"],
    providers: [MonthService, DayService, WeekService, WorkWeekService, ResizeService, DragAndDropService, TimelineViewsService],
    encapsulation: ViewEncapsulation.None,
})
export class CalendarComponent {
    private readonly settings = inject(SwissSettingsService);
    private readonly calendarService = inject(CalendarService);
    private readonly auth = inject(UserAuthenticationService);
    private readonly messageBox = inject(MessageBoxService);
    private readonly datum = inject(DatumFormatPipe);
    private readonly dialog = inject(MatDialog);
    private readonly destroyRef = inject(DestroyRef);
    private readonly timezone = new Timezone();

    readonly appointmentSelected = output<MainDataModels.Termin>();

    /** Emits the selected cell range so the parent can pre-fill new entries; emits `undefined` when the selection is cleared. */
    readonly timeRangeSelected = output<TimeRange | undefined>();

    readonly dateFormat = "dd.MM.yyyy";
    readonly timeFormat = "HH:mm";
    readonly showWeekend = false;
    readonly showQuickInfo = false;
    readonly allowMultiple = true;
    readonly selectedDate = new Date();

    private readonly config$ = this.settings.getSwissAppUserConfig$();
    readonly rasterStart = toSignal(this.config$.pipe(map((config) => config.timeGridStart)), { initialValue: this.settings.getTimeGridStart() });
    readonly rasterEnd = toSignal(this.config$.pipe(map((config) => config.timeGridEnd)), { initialValue: this.settings.getTimeGridEnd() });
    readonly workHours = toSignal(this.config$.pipe(map((config) => ({ highlight: true, start: config.timeGridWorkDayStart, end: config.timeGridWorkDayEnd }) as WorkHoursModel)), {
        initialValue: { highlight: true, start: this.settings.getTimeGridWorkDayStart(), end: this.settings.getTimeGridWorkDayEnd() } as WorkHoursModel,
    });

    private readonly selectedAppointment$ = new BehaviorSubject<MainDataModels.Termin | undefined>(undefined);
    private readonly employees$ = this.calendarService.getEmployees().pipe(shareReplay({ bufferSize: 1, refCount: true }));

    private readonly resources$ = combineLatest([this.employees$, this.calendarService.selectedEmployees$]).pipe(
        map(([all, selected]) => this.toResources(all, selected)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly resources = toSignal(this.resources$, { initialValue: [] as ResourceItem[] });

    readonly resourceGroup = toSignal(this.resources$.pipe(map((resources) => ({ resources: resources.length ? ["Mitarbeiter"] : [] }) as GroupModel)), {
        initialValue: { resources: [] } as GroupModel,
    });

    private readonly calendarData$: Observable<MainDataModels.KalenderDaten | undefined> = this.calendarService.selectedEmployees$.pipe(
        switchMap((employees) => this.calendarService.getCalendarData(employees)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly events = toSignal(
        combineLatest([this.calendarData$, this.selectedAppointment$]).pipe(
            map(([data, selected]) => this.toEvents(data).map((event) => (event.Id === selected?.id ? { ...event, Selected: true } : event))),
            map((dataSource) => ({ allowDeleting: false, dataSource }) as EventSettingsModel),
        ),
        { initialValue: { allowDeleting: false, dataSource: [] } as EventSettingsModel },
    );

    onCellClick(args: CellClickEventArgs): void {
        this.timeRangeSelected.emit({ start: args.startTime, end: args.endTime });
    }

    onEventClick(args: EventClickArgs): void {
        this.timeRangeSelected.emit(undefined);
        const id = this.readEventId(args.event);
        if (!id) {
            this.selectedAppointment$.next(undefined);
            return;
        }
        this.calendarData$
            .pipe(
                take(1),
                map((data) => data?.termine.find((termin) => termin.id === id)),
                filter((appointment): appointment is MainDataModels.Termin => !!appointment),
                tap((appointment) => this.selectedAppointment$.next(appointment)),
                tap((appointment) => this.calendarService.rememberLastAppointment(appointment.id)),
                tap((appointment) => this.appointmentSelected.emit(appointment)),
                catchError((err) =>
                    of(err).pipe(
                        tap((error) => this.logError("onEventClick", error)),
                        switchMap(() => this.recover()),
                    ),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    /** Paint the selected appointment green. Driven by the `Selected` field on the event data, so it survives scrolling and view changes. */
    onEventRendered(args: EventRenderedArgs): void {
        if (args.data["Selected"]) {
            args.element.style.backgroundColor = SELECTED_COLOR;
        }
    }

    onPopupOpen(args: PopupOpenEventArgs): void {
        args.cancel = true;
        const data = args.data as Record<string, unknown> | undefined;
        const id = data?.["Id"] as string | undefined;
        if (id) {
            this.editTermin(id);
        } else {
            this.createTermin(data?.["StartTime"] as Date | undefined, data?.["EndTime"] as Date | undefined);
        }
    }

    /**
     * Persists drag (move) and resize (length change) back to the server. Both actions complete
     * through Syncfusion's `eventChanged` CRUD action — the same event that updates the on-screen
     * appointment — so handling it here covers both without relying on the separate drag/resize stop events.
     */
    onActionComplete(args: ActionEventArgs): void {
        if (args.requestType !== "eventChanged") {
            return;
        }
        const records = Array.isArray(args.data) ? args.data : args.data ? [args.data] : [];
        records.forEach((record) => this.persistTimeChange(record, "onActionComplete"));
    }

    /** Persists a start/end time change from a drag or resize back to the server. */
    private persistTimeChange(data: Record<string, any>, context: string): void {
        const move: MainDataModels.TerminVerschiebung = {
            Id: data["Id"],
            startDate: this.datum.removeTimeFromDate(data["StartTime"]),
            startTime: this.datum.transformOnlyTime(data["StartTime"]),
            endDate: this.datum.removeTimeFromDate(data["EndTime"]),
            endTime: this.datum.transformOnlyTime(data["EndTime"]),
        };
        this.calendarService
            .moveAppointment(move, () => this.messageBox.askYesNoQuestion("MAIN_APP_DIALOG.MOVE_GROUP_APPOINTMENT.msg", "MAIN_APP_DIALOG.MOVE_GROUP_APPOINTMENT.title"))
            .pipe(
                catchError((err) =>
                    of(err).pipe(
                        tap(() => this.messageBox.showErrorByLanguageKey("MAIN_APP_PENDENZEN.ERROR_DIALOG.APPOINTMENT_MOVE_ERROR.text", ERROR_TITLE_KEY)),
                        tap((error) => this.logError(context, error)),
                        switchMap(() => this.recover()),
                    ),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    private createTermin(start?: Date, end?: Date): void {
        this.dialog
            .open(TerminEintragComponent, { disableClose: true, width: "800px", data: this.newTermin(start, end) })
            .afterClosed()
            .pipe(
                filter((result): result is MainDataModels.TerminMitBenutzer => !!result),
                map((result) => this.withGroupId(result)),
                switchMap((result) => this.saveTermin(result, "MAIN_APP_PENDENZEN.APPOINTMENT_SAVED.text", "MAIN_APP_PENDENZEN.ERROR_DIALOG.APPOINTMENT_SAVE_ERROR.text")),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    private editTermin(calendarId: string): void {
        this.calendarService
            .loadAppointmentWithUsers(calendarId)
            .pipe(
                switchMap((loaded) => this.dialog.open(TerminEintragComponent, { disableClose: true, width: "800px", data: loaded }).afterClosed()),
                filter((result): result is MainDataModels.TerminMitBenutzer => !!result),
                switchMap((result) => this.saveTermin(result, "MAIN_APP_PENDENZEN.APPOINTMENT_UPDATED.text", "MAIN_APP_PENDENZEN.ERROR_DIALOG.APPOINTMENT_UPDATE_ERROR.text")),
                catchError((err) =>
                    of(err).pipe(
                        tap((error) => this.logError("editTermin", error)),
                        switchMap(() => this.recover()),
                    ),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    private saveTermin(termin: MainDataModels.TerminMitBenutzer, successKey: string, errorKey: string): Observable<unknown> {
        return this.calendarService.saveAppointment(termin).pipe(
            tap(() => this.messageBox.showSuccessByLanguageKey(successKey, SUCCESS_TITLE_KEY)),
            catchError((err) =>
                of(err).pipe(
                    tap(() => this.messageBox.showErrorByLanguageKey(errorKey, ERROR_TITLE_KEY)),
                    tap((error) => this.logError("saveTermin", error)),
                    switchMap(() => this.recover()),
                ),
            ),
        );
    }

    private newTermin(start?: Date, end?: Date): MainDataModels.TerminMitBenutzer {
        const user = this.auth.getCurrentAuthenticatedUser()!;
        return {
            termin: {
                id: uuidv4(),
                objectID: "",
                adressID: undefined,
                description: "",
                username: user.loginname,
                location: "",
                private: 0,
                reminder: 1,
                reminderNumber: 15,
                notes: "",
                busyStatus: 2,
                startDate: start && this.datum.removeTimeFromDate(start),
                startTime: start && this.datum.transformOnlyTime(start),
                endDate: end && this.datum.removeTimeFromDate(end),
                endTime: end && this.datum.transformOnlyTime(end),
            },
            mitarbeiter: [],
            terminKategorien: [],
            istNeuerTermin: true,
            nurDiesenTerminBearbeiten: false,
        };
    }

    private withGroupId(termin: MainDataModels.TerminMitBenutzer): MainDataModels.TerminMitBenutzer {
        if (termin.mitarbeiter.length > 1) {
            termin.termin.groupID = uuidv4();
        }
        return termin;
    }

    private toResources(employees: MainDataModels.Mitarbeiter[], selected: string[]): ResourceItem[] {
        const chosen = new Set(selected);
        return employees
            .filter((employee) => chosen.has(employee.loginname))
            .map((employee) => ({
                Mitarbeiter: `${employee.firstname} ${employee.lastname}`,
                Farbe: employee.calendarHexColor || PASTEL_TONES[Math.floor(Math.random() * PASTEL_TONES.length)],
                UserName: employee.loginname,
            }));
    }

    private toEvents(data: MainDataModels.KalenderDaten | undefined): ScheduleEvent[] {
        if (!data?.termine) {
            return [];
        }
        return data.termine.map((termin) => this.toEvent(termin, data)).filter((event): event is ScheduleEvent => event !== null);
    }

    private toEvent(termin: MainDataModels.Termin, data: MainDataModels.KalenderDaten): ScheduleEvent | null {
        try {
            const series = data.terminSerien?.find((serie) => serie.calendarId === termin.id);
            return series ? this.toRecurringEvent(termin, series, data) : this.toSingleEvent(termin);
        } catch (error) {
            this.logError("toEvent", error);
            return null;
        }
    }

    private toSingleEvent(termin: MainDataModels.Termin): ScheduleEvent {
        const start = this.combine(termin.startDate, termin.startTime);
        const end = this.combine(termin.endDate, termin.endTime);
        return { Id: termin.id, Subject: termin.description || DEFAULT_SUBJECT, StartTime: start, EndTime: end, IsAllDay: this.isAllDay(start, end), UserName: termin.username };
    }

    private toRecurringEvent(termin: MainDataModels.Termin, series: MainDataModels.TerminSerie, data: MainDataModels.KalenderDaten): ScheduleEvent | null {
        if (!series.startDate || !series.endDate) {
            return null;
        }
        const seriesStart = this.timezone.add(new Date(series.startDate), ZURICH);
        const start = this.combine(seriesStart, termin.startTime);
        const end = this.combine(seriesStart, termin.endTime);
        return {
            Id: termin.id,
            Subject: termin.description || DEFAULT_SUBJECT,
            StartTime: start,
            EndTime: end,
            IsAllDay: this.isAllDay(start, end),
            UserName: termin.username,
            RecurrenceRule: this.calendarService.getRecurrenceRule(termin, series),
            RecurrenceException: this.toRecurrenceException(data.terminSerieAusnahmen, termin.id),
        };
    }

    private toRecurrenceException(exceptions: MainDataModels.TerminSerieAusnahme[] | undefined, calendarId: string): string | undefined {
        const dates = (exceptions ?? [])
            .filter((exception) => exception.calendarID === calendarId && (exception.type === 0 || exception.type === 1))
            .map((exception) => this.datum.getSyncfusionCalendarDateStr(new Date(exception.originalDate)));
        return dates.length ? dates.join(",") : undefined;
    }

    private combine(date: Date | undefined, time: string | undefined): Date {
        return this.datum.addTimeToDate(date ?? this.selectedDate, this.normalizeTime(time));
    }

    /** Guards against empty/malformed time strings (e.g. `""`), which otherwise throw in `addTimeToDate`. */
    private normalizeTime(time: string | undefined): string {
        const trimmed = time?.trim();
        return trimmed && /^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed) ? trimmed : "00:00";
    }

    private isAllDay(start: Date, end: Date): boolean {
        return start.getTime() === end.getTime() && start.getHours() === 0 && start.getMinutes() === 0 && start.getSeconds() === 0;
    }

    private readEventId(event: EventClickArgs["event"]): string | undefined {
        const single = Array.isArray(event) ? event[0] : event;
        return single?.["Id"] as string | undefined;
    }

    /** Central error logger for this component. */
    private logError(context: string, error: unknown): void {
        console.error(`calendar.component.ts; ${context}();`, error);
    }

    /** Terminal for `catchError` pipelines: recovers by completing without a value. */
    private recover(): Observable<never> {
        return EMPTY;
    }
}
